import React from 'react';

import BiddingCard from './BiddingCard';
import {
  formatMoneyWithoutCurrency,
  inclVATtoExclVAT
} from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import {
  AuctionFilledIcon,
  BidIcon,
  LeaderIcon,
  SellIcon
} from '@kvdbil/components';

export interface BiddingMessageProps {
  winningLink: string;

  checkoutOrderId?: number;
  isCheckoutComplete?: boolean;

  isWinner: boolean;
  isParticipating: boolean;
  hasCheckoutOrder: boolean;
  reservationPriceReached: boolean;

  releasingCost: number;
  leadingBidAmount: number;
  hasBids: boolean;
  vatExcludedBiddingAllowed: boolean;
}

export const BiddingMessage = ({
  winningLink,
  isParticipating,
  reservationPriceReached,
  isWinner,
  leadingBidAmount,
  releasingCost,
  checkoutOrderId,
  isCheckoutComplete,
  hasCheckoutOrder,
  hasBids,
  vatExcludedBiddingAllowed
}: BiddingMessageProps) => {
  const { t } = useTranslation();
  const buttonUrl = hasCheckoutOrder
    ? winningLink
    : '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_MY_AUCTIONS/ROUTES.MY_ACCOUNT_MY_AUCTIONS_WON';
  const buttonText = hasCheckoutOrder
    ? t('WINNER_PAYMENT_BUTTON_TEXT')
    : t('Go to my pages');

  const leadingBidAmountNormalized = vatExcludedBiddingAllowed
    ? inclVATtoExclVAT(leadingBidAmount)
    : leadingBidAmount;

  return (
    <div>
      {isWinner && reservationPriceReached && (
        <BiddingCard
          icon={<LeaderIcon />}
          color="primary"
          isCheckoutComplete={isCheckoutComplete}
          checkoutOrderId={checkoutOrderId}
          heading={t('WINNER_HEADING')}
          label={t('WINNER_LABEL')}
          price={{
            amount: formatMoneyWithoutCurrency(leadingBidAmountNormalized),
            currency: t('SEK')
          }}
          releasingCost={releasingCost}
          additionalInformation={[
            {
              title: t('WINNER_TEXT_TITLE_ABOVE_RES'),
              text: t('WINNER_TEXT')
            }
          ]}
          buttonUrl={buttonUrl}
          buttonText={buttonText}
          hasBids={hasBids}
        />
      )}

      {isWinner && !reservationPriceReached && (
        <BiddingCard
          icon={<AuctionFilledIcon />}
          color="primary"
          heading={t('WINNER_HEADING_BELOW_RES')}
          label={t('WINNER_LABEL_BELOW_RES')}
          price={{
            amount: formatMoneyWithoutCurrency(leadingBidAmountNormalized),
            currency: t('SEK')
          }}
          releasingCost={releasingCost}
          additionalInformation={[
            {
              title: t('WINNER_TEXT_TITLE_BELOW_RES'),
              text: t('WINNER_TEXT_BELOW_RES')
            },
            {
              title: t('WINNER_TEXT_TITLE_NEXT_STEP'),
              text: t('WINNER_TEXT_BELOW_RES_NEXT_STEP')
            }
          ]}
          hasBids={hasBids}
        />
      )}

      {!isWinner && isParticipating && (
        <BiddingCard
          icon={<SellIcon />}
          heading={t('LOSER_HEADING')}
          label={t('LOSER_LABEL')}
          price={{
            amount: formatMoneyWithoutCurrency(leadingBidAmountNormalized),
            currency: t('SEK')
          }}
          releasingCost={releasingCost}
          hasBids={hasBids}
        />
      )}

      {!isParticipating && (
        <BiddingCard
          icon={<BidIcon />}
          heading={t('NOT_PARTICIPATING_HEADING')}
          label={t('NOT_PARTICIPATING_LABEL')}
          price={{
            amount: formatMoneyWithoutCurrency(leadingBidAmountNormalized),
            currency: t('SEK')
          }}
          releasingCost={releasingCost}
          hasBids={hasBids}
        />
      )}
    </div>
  );
};

export default BiddingMessage;
